import { initialize, openSession, changeUser, getUser, logCustomEvent, logPurchase, User } from '@braze/web-sdk'

import config from '@/config'
import { ENV_DEV_OR_STAGING } from '@/helpers/environment'

export default {
	install(Vue, { apiKey, baseUrl, store }) {
		let initialized = false
		const brazeHost = 'https://rest.iad-05.braze.com'

		const getInstance = () => {
			if (!initialized) {
				initialize(apiKey, {
					baseUrl,
					noCookies: true
				})

				openSession()

				if (store.getters['isAuthenticated'] || store.getters['isOnboarded']) {
					changeUser(store.getters['getUserId'])
				}

				initialized = true
			}

			return { getUser, changeUser, logCustomEvent, logPurchase }
		}

		const addToCustomAttributeArray = (key, value) => {
			getInstance().getUser().addToCustomAttributeArray(key, value)
		}

		const setCustomUserAttribute = (key, value) => {
			getInstance().getUser().setCustomUserAttribute(key, value)
		}

		const makeRequest = (endpoint, params) => {
			return fetch(`${brazeHost}${endpoint}`, {
				body: JSON.stringify(params),
				headers: {
					Authorization: `Bearer ${config('BrazeCanvasApiKey')}`,
					'Content-Type': 'application/json'
				},
				method: 'POST'
			})
		}

		const appboyMethods = {
			changeUser(user_id) {
				getInstance().changeUser(user_id)
			},

			/**
			 * Create user in Braze after registration
			 * @param {object} data
			 * @param {'male'|'female'} data.gender
			 * @param {string} data.email
			 * @param {string} data.currency
			 * @param {boolean} data.isEmailSubscriptionDisabled
			 * @param {'email_submit_short_plans'|'email_submit'} data.emailSubmitEvent
			 */
			logRegistration(data) {
				if (data.gender) {
					getInstance().getUser().setGender(User.Genders[data.gender.toUpperCase()])
				}

				setCustomUserAttribute('currency', data.currency)
				getInstance().getUser().setEmail(data.email)
				getInstance().getUser().addAlias(data.email, 'email')

				if (data.isEmailSubscriptionDisabled) {
					getInstance().getUser().setEmailNotificationSubscriptionType(User.NotificationSubscriptionTypes.UNSUBSCRIBED)
				}

				if (data.emailSubmitEvent) {
					getInstance().logCustomEvent(data.emailSubmitEvent, { email: data.email })
				}
			},

			addToCustomAttributeArray(key, value) {
				addToCustomAttributeArray(key, value)
			},

			setCustomUserAttribute(key, value) {
				setCustomUserAttribute(key, value)
			},

			logCustomEvent(event_name, properties = {}) {
				getInstance().logCustomEvent(event_name, properties)
			},

			setFirstName(name) {
				getInstance().getUser().setFirstName(name)
			},

			logPurchase(plan_id, price, currency = 'USD', quantity = 1) {
				getInstance().logPurchase(plan_id, price, currency, quantity)
			},

			canvas(canvas_id, user_id, canvas_entry_properties = {}) {
				return makeRequest('/canvas/trigger/send', {
					canvas_id,
					recipients: [
						{
							external_user_id: user_id,
							canvas_entry_properties
						}
					]
				})
			},
			/**
			 * Doc: https://www.braze.com/docs/api/endpoints/messaging/schedule_messages/post_schedule_triggered_canvases/
			 */
			canvasShedule({ canvas_id, user_id, canvas_entry_properties = {}, time }) {
				return makeRequest('/canvas/trigger/schedule/create', {
					canvas_id,
					recipients: [
						{
							external_user_id: user_id,
							canvas_entry_properties
						}
					],
					schedule: {
						time
					}
				})
			},

			triggerCampaign(campaign_id, user_id, trigger_properties = {}) {
				return fetch('https://rest.iad-05.braze.com/campaigns/trigger/send', {
					body: JSON.stringify({
						campaign_id,
						recipients: [
							{
								external_user_id: user_id,
								trigger_properties
							}
						]
					}),
					headers: {
						Authorization: `Bearer ${config('BrazeCampaignApiKey')}`,
						'Content-Type': 'application/json'
					},
					method: 'POST'
				})
			}
		}

		Vue.prototype.$appboy = appboyMethods

		if (ENV_DEV_OR_STAGING) {
			Object.keys(Vue.prototype.$appboy).forEach((methodName) => {
				Vue.prototype.$appboy[methodName] = new Proxy(appboyMethods[methodName], {
					apply(method, thisObject, argsList) {
						/* eslint-disable no-console */
						console.groupCollapsed(`%cBraze [debug]: %c${method.name}`, 'font-weight: 400', 'color: #ff00cc')
						console.log({ method: method.name, arguments: argsList })
						console.groupEnd()
						/* eslint-enable no-console */

						return method.apply(thisObject, argsList)
					}
				})
			})
		}
	}
}
